import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircle2, ClipboardList, MapPin, Calendar, Activity, Loader2 } from 'lucide-react';
import { db } from '../Firebase';
import { collection, onSnapshot } from 'firebase/firestore';

export default function Projects() {
  const [completedProjects, setCompletedProjects] = useState([]);
  const [upcomingRoadmap, setUpcomingRoadmap] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProjectId, setFilterProjectId] = useState(null);

  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.selectedProjectId) {
      setFilterProjectId(location.state.selectedProjectId);
    }
  }, [location.state]);

  useEffect(() => {
    if (filterProjectId && completedProjects.length > 0) {
      const timer = setTimeout(() => {
        const element = document.getElementById(`project-card-${filterProjectId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [filterProjectId, completedProjects]);

  useEffect(() => {
    const compRef = collection(db, 'completed_projects');
    const upRef = collection(db, 'upcoming_projects');

    const unsubscribeComp = onSnapshot(compRef, (snapshot) => {
      const comps = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      comps.sort((a, b) => {
        const timeA = a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt).getTime()) : 0;
        const timeB = b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt).getTime()) : 0;
        return timeB - timeA;
      });
      const uniqueComps = [];
      const seen = new Set();
      for (const c of comps) {
        if (c.title) {
          const norm = c.title.trim().toLowerCase();
          if (!seen.has(norm)) {
            seen.add(norm);
            uniqueComps.push(c);
          }
        }
      }
      setCompletedProjects(uniqueComps);
      setLoading(false);
    }, (err) => {
      console.error("Error loading completed projects:", err);
      setLoading(false);
    });

    const unsubscribeUp = onSnapshot(upRef, (snapshot) => {
      const ups = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      ups.sort((a, b) => (a.num || '').localeCompare(b.num || ''));
      const uniqueUps = [];
      const seen = new Set();
      for (const u of ups) {
        if (u.hosp) {
          const norm = u.hosp.trim().toLowerCase();
          if (!seen.has(norm)) {
            seen.add(norm);
            uniqueUps.push(u);
          }
        }
      }
      setUpcomingRoadmap(uniqueUps);
      setLoading(false);
    }, (err) => {
      console.error("Error loading upcoming projects:", err);
      setLoading(false);
    });

    return () => {
      unsubscribeComp();
      unsubscribeUp();
    };
  }, []);

  if (loading) {
    return (
      <div className="font-sans bg-brand-light min-h-screen flex flex-col items-center justify-center space-y-4">
        <Loader2 className="animate-spin text-brand-blue" size={40} />
        <p className="text-sm font-semibold tracking-wider font-poppins text-brand-navy/60">Loading Projects Showcase...</p>
      </div>
    );
  }

  return (
    <div className="font-sans bg-brand-light">
      
      {/* Page Header */}
      <section className="bg-brand-navy text-white pt-28 pb-16 relative overflow-hidden grid-mesh">
        <div className="absolute inset-0 bg-brand-navy/80 z-10" />
        <div className="absolute inset-0 z-0 bg-brand-navy/40" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20 text-center space-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-green font-poppins block">Footprint</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold font-montserrat text-white tracking-tight">
            Our Healthcare Installations
          </h1>
          <p className="text-sm text-brand-silver max-w-2xl mx-auto font-poppins">
            Explore authentic case histories of completed modular operation theatres, ICU gas pipelines, and bulk furniture setups serving premier doctors.
          </p>
        </div>
      </section>

      {/* Completed Projects Showcase */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {completedProjects.map((p, idx) => {
              const isHighlighted = filterProjectId === p.id;
              return (
                <div 
                  key={idx} 
                  id={`project-card-${p.id}`}
                  className={`flex flex-col h-full bg-white rounded-3xl transition-all duration-500 border group overflow-hidden ${
                    isHighlighted 
                      ? 'border-2 border-brand-blue shadow-2xl ring-4 ring-brand-blue/15 scale-[1.02] -translate-y-1' 
                      : 'border-brand-silver/50 shadow-md hover:shadow-2xl hover:border-brand-blue/30 hover:-translate-y-1.5'
                  }`}
                >
                  {/* Image / Graphic Info Container */}
                  <div className="h-52 bg-brand-light relative overflow-hidden shrink-0">
                    {p.image && !p.image.startsWith('/assets/logo1.png') ? (
                      <img 
                        src={p.image} 
                        alt={p.title} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-brand-navy to-brand-blue relative flex flex-col justify-between p-6">
                        <div className="absolute inset-0 bg-grid-mesh opacity-10" />
                        <div className="absolute -top-12 -right-12 w-36 h-36 bg-brand-green/20 rounded-full blur-2xl group-hover:bg-brand-blue/30 transition-all duration-500" />
                        
                        <div className="relative z-10 flex justify-between items-start">
                          <span className="text-[9px] font-bold text-brand-green uppercase tracking-widest border border-brand-green/30 px-2 py-0.5 rounded-full bg-brand-green/10 font-poppins">
                            Completed
                          </span>
                          <Activity size={24} className="text-brand-blue" />
                        </div>
    
                        <div className="relative z-10 flex items-center justify-between text-[10px] font-semibold font-poppins text-brand-green/90">
                          <span className="flex items-center gap-1">
                            <CheckCircle2 size={12} /> 100% Quality
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Overlapping top-left location badge */}
                    <div className="absolute top-3 left-3 flex items-center gap-1 bg-brand-navy/90 text-brand-silver text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg shadow-md border border-white/5 font-poppins z-10">
                      <MapPin size={10} className="text-brand-blue" /> {p.location}
                    </div>
                  </div>

                  {/* Card Content Area */}
                  <div className="p-6 flex-grow flex flex-col justify-between space-y-5">
                    <div className="space-y-3.5">
                      <div className="space-y-1">
                        <span className="text-[11px] font-bold text-brand-green uppercase tracking-widest font-poppins flex items-center gap-1">
                          <Calendar size={11} /> {p.timeline}
                        </span>
                        <h3 className={`text-xl font-bold font-montserrat tracking-tight leading-tight transition-colors duration-300 ${
                          isHighlighted ? 'text-brand-blue' : 'text-brand-navy group-hover:text-brand-blue'
                        }`}>
                          {p.title}
                        </h3>
                        <p className="text-xs text-brand-blue font-bold font-poppins leading-snug">{p.authority}</p>
                      </div>

                      <p className="text-sm text-gray-500 font-poppins leading-relaxed font-light line-clamp-3">
                        {p.desc}
                      </p>

                      {/* Infrastructure Matrix */}
                      <div className="space-y-2 pt-4 border-t border-brand-silver/40">
                        <h4 className="text-xs font-bold font-montserrat uppercase tracking-wider text-brand-navy flex items-center gap-1.5">
                          <ClipboardList size={12} className="text-brand-blue" />
                          Infrastructure Matrix
                        </h4>
                        <ul className="space-y-1.5 text-xs text-gray-500 font-poppins">
                          {p.supplies.map((item, keyIdx) => (
                            <li key={keyIdx} className="flex gap-2 items-start hover:text-brand-navy transition-colors duration-200">
                              <CheckCircle2 size={13} className="text-brand-green shrink-0 mt-0.5" />
                              <span className="leading-snug line-clamp-2">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Upcoming Projects Roadmap timeline */}
      <section className="py-20 bg-brand-light border-t border-brand-silver/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-blue font-poppins block">On the horizon</span>
            <h2 className="text-3xl font-bold font-montserrat text-brand-navy">Upcoming Project Timeline</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-[28px] left-[12%] right-[12%] h-0.5 bg-brand-blue/20 z-0" />
            
            {upcomingRoadmap.map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl border border-brand-silver/30 shadow-sm relative z-10 text-center space-y-4 hover:-translate-y-1 transition-transform duration-300">
                <div className="w-10 h-10 rounded-full bg-brand-navy text-brand-green flex items-center justify-center font-extrabold text-sm mx-auto shadow-md border-2 border-white">
                  {item.num}
                </div>
                <div>
                  <h3 className="font-extrabold text-sm text-brand-navy font-montserrat leading-tight">{item.hosp}</h3>
                  <span className="text-[10px] text-brand-blue font-bold font-poppins uppercase tracking-wider block mt-1">{item.loc}</span>
                </div>
                <p className="text-xs text-gray-500 font-poppins leading-relaxed">{item.scope}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
